import { useDeepCompareMemo } from '@react-hookz/web/useDeepCompareMemo/index.js';
import type { Route } from 'next';
import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';
import type { WithContext, MediaObject } from 'schema-dts';

import { buildImageHoverTitle } from '#cutils/builders/buildImageHoverTitle';
import { MediaContextPlaybackStatus } from '#cutils/context/MediaContext';
import { useMediaState } from '#cutils/hooks/useMediaContext';
import { notNullOrUndefined } from '#cutils/misc/notNullOrUndefined';

import { AspectRatio } from '../AspectRatio';
import { SchemaOrg } from '../SchemaOrg';

import { ArdPlayer } from './ArdPlayer';
import type { MediaCollection } from './ArdPlayer/ArdPlayerInternal';
import styles from './MediaPlayer.module.scss';
import { MediaOverlay } from './Shared/MediaOverlay';

export type MediaPlayerMedia = {
	/**
	 * The type of the media
	 */
	type: 'audio' | 'video' | 'livestream';
	/**
	 * The json schema data for the media resource
	 */
	structuredData?: WithContext<MediaObject> | undefined | null;

	/**
	 * The media collection to be passed to the ARD player.
	 */
	mediaCollection?: MediaCollection | undefined;
};

export type MediaPlayerPosterImage = {
	/**
	 * The alt text for the thumbnail image.
	 */
	altText?: string | null;
	/**
	 * The copyright information for the thumbnail image.
	 */
	copyright?: string | null;
	/**
	 * The link for the thumbnail image.
	 */
	link?: Route | null;
	/**
	 * The URL of the thumbnail image.
	 */
	imageUrl?: string | null;
	/**
	 * Indicates whether the media content is a priority.
	 */
	isPriority?: boolean;
	/**
	 * Indicates whether the media content is a hero.
	 */
	isHero?: boolean;
	/**
	 * set to `true` to enable the image zooming effect.
	 */
	enableZoomEffect?: boolean;
	/**
	 * The sizes string for the poster images
	 */
	sizes: string;
	/**
	 * Title of the play button
	 */
	playButtonTitle?: string;
	/**
	 * If set, displays the component on top of the poster image
	 */
	title?: ReactNode;
	/**
	 * Set to true to show a linear gradient to be able to read the text easier.
	 */
	showGradient: boolean;
};

type Props = {
	/**
	 * The media information to be used to set up the player.
	 *
	 * If the information is missing, only the poster image will be displayed and the media can not be played back.
	 */
	media: MediaPlayerMedia;

	/**
	 * The information needed to display the poster image.
	 */
	posterImage: MediaPlayerPosterImage;

	/**
	 * The ID of the played content, is needed for the analytics team.
	 */
	rowId: number | string | undefined;

	/**
	 * Callback function to handle changes in the play status.
	 *
	 * @param status the new play status
	 */
	onPlayStatusChange?: (status: MediaContextPlaybackStatus) => void;

	/**
	 * Callback function to report buffering progress.
	 *
	 */
	onBuffering?: () => void;
};

const statusClassMap = {
	[MediaContextPlaybackStatus.PAUSED]: styles.paused,
	[MediaContextPlaybackStatus.STARTED]: styles.playing,
	[MediaContextPlaybackStatus.STOPPED]: styles.stopped,
};

const typeClassMap = {
	audio: styles.audio,
	video: undefined,
	livestream: undefined,
};

/**
 * Returns the class name corresponding to the given media playback status.
 *
 * @param mediaPlaybackStatus The media playback status.
 * @return The class name corresponding to the media playback status.
 */
function getContextClasses(mediaPlaybackStatus: MediaContextPlaybackStatus, type: 'audio' | 'video' | 'livestream') {
	return [statusClassMap[mediaPlaybackStatus], typeClassMap[type]].filter(notNullOrUndefined).join(' ');
}

function getPlayerStatusFromMediaState(mediaStatus: MediaContextPlaybackStatus) {
	switch (mediaStatus) {
		case MediaContextPlaybackStatus.PAUSED:
			return 'pause';
		case MediaContextPlaybackStatus.STARTED:
			return 'play';
		case MediaContextPlaybackStatus.STOPPED:
			return 'stop';
	}
}

export function MediaPlayer({ media, posterImage, onPlayStatusChange, onBuffering }: Props) {
	const [isPlayerInitialized, setIsPlayerInitialized] = useState(false);

	const onMediaStatusChange = useCallback((status: MediaContextPlaybackStatus) => {
		if (status === MediaContextPlaybackStatus.STARTED) {
			setIsPlayerInitialized(true);
		}
	}, []);

	const { mediaPlaybackStatus, mediaPause, mediaStart, mediaStop } = useMediaState(onMediaStatusChange);

	const handleStreamStop = useCallback(() => {
		onPlayStatusChange?.(MediaContextPlaybackStatus.STOPPED);
		mediaStop();
	}, [mediaStop, onPlayStatusChange]);

	const handleStreamPause = useCallback(
		({ ended }: { ended: boolean }) => {
			if (!ended && mediaPlaybackStatus !== MediaContextPlaybackStatus.STOPPED) {
				onPlayStatusChange?.(MediaContextPlaybackStatus.PAUSED);
				mediaPause();
			}
		},
		[mediaPlaybackStatus, mediaPause, onPlayStatusChange]
	);

	const handleStreamStart = useCallback(() => {
		onPlayStatusChange?.(MediaContextPlaybackStatus.STARTED);
		mediaStart();
	}, [mediaStart, onPlayStatusChange]);

	const mediaCollection = useDeepCompareMemo(() => {
		return media.mediaCollection;
	}, [media.mediaCollection]);

	const playerStatusClass = getContextClasses(mediaPlaybackStatus, media.type);

	const mediaTitle = buildImageHoverTitle({
		altText: posterImage.altText,
		copyright: posterImage.copyright,
		variant: 'bild',
	});

	// show the play button when the media is stopped and the media is defined
	const showPlayButton = mediaPlaybackStatus === MediaContextPlaybackStatus.STOPPED && !!media.mediaCollection;

	return (
		<AspectRatio as="figure" className={playerStatusClass}>
			{/* the AspectRation component can only have a single child */}
			<div>
				{media.structuredData ? <SchemaOrg schema={media.structuredData} /> : null}

				<MediaOverlay
					className={styles.overlay}
					buttonVariant={media.type}
					isHero={!!posterImage.isHero}
					showPlayButton={showPlayButton}
					onPlayButtonClick={mediaStart}
					isPriority={posterImage.isPriority}
					thumbnailAltText={posterImage.altText}
					thumbnailCopyright={posterImage.copyright}
					thumbnailLink={posterImage.link}
					thumbnailTitle={mediaTitle}
					thumbnailUrl={posterImage.imageUrl}
					enableZoomEffect={posterImage.enableZoomEffect ?? false}
					sizes={posterImage.sizes}
					buttonTitle={posterImage.playButtonTitle}
					showGradient={posterImage.showGradient}
				>
					{posterImage.title ? posterImage.title : null}
				</MediaOverlay>

				{mediaCollection && isPlayerInitialized ? (
					<ArdPlayer
						className={styles.player}
						mediaCollection={mediaCollection}
						playStatus={getPlayerStatusFromMediaState(mediaPlaybackStatus)}
						onBuffering={onBuffering}
						// TODO add EVENT_PLAY_STREAM
						onInitialFrame={handleStreamStart}
						onPauseStream={handleStreamPause}
						onStopStream={handleStreamStop}
					/>
				) : null}
			</div>
		</AspectRatio>
	);
}
